import React, { useState, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  Button
} from '@mui/material';
import MicIcon from '@mui/icons-material/Mic';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { ACTIVE_BUTTON_BLUE } from 'constants/ColorConstants.js'

const ChatInput = ({ 
  onSendMessage, 
  disabled = false, 
  placeholder = "Type your message...",
  showAttachments = false,
  showVoice = false
}) => {
  const [message, setMessage] = useState('');
  const [isMultiline, setIsMultiline] = useState(false);
  const inputRef = useRef(null);

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message);
      setMessage('');
      setIsMultiline(false);
      // Focus back to input after sending
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      if (event.shiftKey) {
        // Shift+Enter creates new line
        setIsMultiline(true);
      } else {
        // Enter sends message
        event.preventDefault();
        handleSend();
      }
    }
  };

  const handleInputChange = (event) => {
    const value = event.target.value;
    setMessage(value);
    
    // Auto-detect multiline
    setIsMultiline(value.includes('\n'));
  };

  const handleAttachment = () => {
    // TODO: Implement file attachment
    console.log('Attachment clicked');
  };

  const handleVoiceInput = () => {
    // TODO: Implement voice input
    console.log('Voice input clicked');
  };

  return (
    <Paper
      elevation={0}
      sx={{
        padding: 2,
        backgroundColor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}
      >
        {/* Optional attachment button */}
        {showAttachments && (
          <Tooltip title="Attach file">
            <IconButton
              size="small"
              onClick={handleAttachment}
              disabled={disabled}
              sx={{ marginBottom: 0.5 }}
            >
              <AttachFileIcon/>
            </IconButton>
          </Tooltip>
        )}

        {/* Text input */}
        <TextField
          ref={inputRef}
          fullWidth
          multiline
          maxRows={4}
          value={message}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 0,
              backgroundColor: 'white',
              fontSize: 14,
              minHeight: '40px',
              '&:hover': {
                backgroundColor: 'white',
              },
              '&.Mui-focused': {
                backgroundColor: 'background.paper',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: ACTIVE_BUTTON_BLUE,
              }
            }
          }}
          slotProps={{
            input: {
              endAdornment: showVoice && message.trim() === '' && (
                <Tooltip title="Voice input">
                  <IconButton
                    size="small"
                    onClick={handleVoiceInput}
                    disabled={disabled}
                  >
                    <MicIcon />
                  </IconButton>
                </Tooltip>
              )
            }
          }}
        />

        {/* Send button */}
        <Button
          onClick={handleSend}
          disabled={disabled || !message.trim()}
          variant="contained"
          disableElevation
          sx={{
            backgroundColor: ACTIVE_BUTTON_BLUE,
            color: 'white',
            minWidth: 'auto',
            paddingX: 2,
            height: '40px',
            borderRadius: '3px',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: 500,
            '&:hover': {
              // backgroundColor: AI_PRIMARY_DARK,
            },
            '&.Mui-disabled': {
              backgroundColor: 'grey.300',
              color: 'grey.500',
            }
          }}
        >
          Ask
        </Button>
      </Box>

      {/* Helper text */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: 0.5,
            paddingX: 1
          }}
        >
          <Box
            sx={{
              fontSize: '10px', // More readable helper text
              color: 'text.secondary',
              opacity: 0.7
            }}
          >
            AI can make mistakes. Please double check answers.
          </Box>
        </Box>
    </Paper>
  );
};

export default ChatInput;